import { FC, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { UnifiedMarkdown } from "~/components/UnifiedMarkdown";
import type {
  SectionView,
  TemplateSectionsPhase,
} from "~/features/cases/hooks/useFtueTemplateSectionsView";
import { useTheme } from "~/hooks/useTheme";

const SKY_BLUE = "#2563eb";
const SKELETON_PLACEHOLDER_COUNT = 4;
const ENTER_STAGGER_MS = 80;

interface LiveSectionsViewProps {
  sections: SectionView[];
  phase: TemplateSectionsPhase;
}

interface ContainerStyleVars {
  panelBg: string;
  borderColor: string;
  skeletonRgb: string;
}

function useContainerStyle(): ContainerStyleVars {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== "light";
  return {
    panelBg: isDark ? "#141416" : "hsl(var(--muted))",
    borderColor: isDark ? "rgba(255,255,255,0.08)" : "hsl(var(--border))",
    skeletonRgb: isDark ? "255,255,255" : "0,0,0",
  };
}

/**
 * Right-pane visualization driven by real template + report data.
 *
 * - When `phase === "reading"` and there are no sections yet, we render a small
 *   set of generic skeleton cards so the panel doesn't collapse to empty.
 * - Otherwise, we render one card per template section, with appearance
 *   transitions staggered by index. Status comes from the merged view hook.
 */
export const LiveSectionsView: FC<LiveSectionsViewProps> = ({
  sections,
  phase,
}) => {
  const { panelBg, borderColor, skeletonRgb } = useContainerStyle();

  // When the template is still being read and we have no real sections,
  // render placeholder skeletons to fill the panel.
  const placeholderCount = useMemo(() => {
    if (sections.length > 0) return 0;
    return phase === "reading" ? SKELETON_PLACEHOLDER_COUNT : 0;
  }, [sections.length, phase]);

  return (
    <div
      aria-live="polite"
      style={{
        flex: 1,
        height: "100%",
        overflow: "auto",
        borderRadius: 8,
        border: `1px solid ${borderColor}`,
        background: panelBg,
        padding: "28px 32px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `radial-gradient(circle, rgba(${skeletonRgb},0.03) 1px, transparent 1px)`,
          backgroundSize: "24px 24px",
          maskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 100%)",
        }}
      />

      {sections.length === 0
        ? Array.from({ length: placeholderCount }).map((_, idx) => (
            <SectionSkeletonCard key={`skeleton-${idx}`} index={idx} />
          ))
        : sections.map((section, idx) => (
            <SectionCard key={section.id} section={section} index={idx} />
          ))}
    </div>
  );
};

interface SectionCardProps {
  section: SectionView;
  index: number;
}

const SectionCard: FC<SectionCardProps> = ({ section, index }) => {
  const { skeletonRgb } = useContainerStyle();

  const isActive = section.status === "processing";
  const isCompleted = section.status === "completed";
  const isFailed = section.status === "failed";
  const isSplitting = section.status === "splitting";
  const accentColor = isFailed
    ? "rgba(220,38,38,0.85)"
    : isActive
      ? SKY_BLUE
      : isCompleted
        ? "rgba(37,99,235,0.5)"
        : `rgba(${skeletonRgb},0.10)`;

  const enterDelay = index * ENTER_STAGGER_MS;

  return (
    <div
      style={{
        position: "relative",
        paddingLeft: 18,
        zIndex: isActive ? 2 : 1,
        opacity: 0,
        transform: "translateY(12px) scale(0.98)",
        animation: `cascade-card-enter 520ms cubic-bezier(0.22, 1, 0.36, 1) ${enterDelay}ms forwards`,
      }}
    >
      {isActive && (
        <div
          className="cascade-animate"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: "-10px -16px",
            background:
              "radial-gradient(ellipse at left, rgba(37,99,235,0.18) 0%, rgba(37,99,235,0.05) 40%, transparent 70%)",
            pointerEvents: "none",
            borderRadius: 12,
            animation: "cascade-halo-breathe 2.4s ease-in-out infinite",
          }}
        />
      )}

      <div
        style={{
          position: "absolute",
          left: 0,
          top: 2,
          bottom: 2,
          width: 2,
          borderRadius: 2,
          background: accentColor,
          boxShadow: isActive ? `0 0 12px 1px rgba(37,99,235,0.6)` : "none",
          transition: "background 280ms ease, box-shadow 280ms ease",
        }}
      />

      <SectionHeader section={section} />

      <SectionBody section={section} />

      {isSplitting && <SplittingSkeletonLines />}
    </div>
  );
};

interface SectionHeaderProps {
  section: SectionView;
}

const SectionHeader: FC<SectionHeaderProps> = ({ section }) => {
  const isActive = section.status === "processing";
  const isCompleted = section.status === "completed";
  const isFailed = section.status === "failed";

  return (
    <div
      className="mb-1 flex items-center gap-2"
      style={{ position: "relative" }}
    >
      <span
        className="inline-flex items-center rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400"
        aria-hidden="true"
      >
        <FormattedMessage
          id="ftue.templateBuild.sectionBadge"
          defaultMessage="Section {n}"
          values={{ n: section.order }}
        />
      </span>
      <h3
        className="text-sm font-medium text-foreground leading-snug"
        style={{ margin: 0 }}
      >
        {section.title}
      </h3>

      {isActive && <ThinkingDots />}
      {isCompleted && <DoneCheck />}
      {isFailed && (
        <span className="text-[10px] font-medium text-destructive uppercase tracking-wide">
          <FormattedMessage
            id="ftue.templateBuild.section.failed"
            defaultMessage="Generation failed"
          />
        </span>
      )}
    </div>
  );
};

interface SectionBodyProps {
  section: SectionView;
}

const SectionBody: FC<SectionBodyProps> = ({ section }) => {
  if (section.status === "completed" && section.content) {
    return (
      <div className="mt-1">
        <UnifiedMarkdown className="prose prose-sm dark:prose-invert max-w-none text-xs">
          {section.content}
        </UnifiedMarkdown>
      </div>
    );
  }

  if (section.status === "failed") {
    return (
      <p className="text-xs text-muted-foreground mt-1 italic">
        <FormattedMessage
          id="ftue.templateBuild.section.failedDescription"
          defaultMessage="Generation failed for this section. The other sections will continue."
        />
      </p>
    );
  }

  if (section.status === "splitting") {
    // The body for a splitting section is rendered separately
    // by SplittingSkeletonLines for stronger visual separation.
    return null;
  }

  const promptPreview = section.promptPreview || section.fullPrompt;
  if (!promptPreview) return null;

  return (
    <p
      className="text-xs text-muted-foreground mt-1 leading-relaxed"
      style={{
        whiteSpace: "pre-line",
        opacity: section.status === "processing" ? 0.9 : 0.7,
      }}
    >
      {promptPreview}
    </p>
  );
};

const ThinkingDots: FC = () => (
  <div
    aria-hidden="true"
    style={{
      display: "flex",
      gap: 3,
      alignItems: "center",
    }}
  >
    {[0, 1, 2].map((d) => (
      <span
        key={d}
        className="cascade-animate"
        style={{
          width: 4,
          height: 4,
          borderRadius: 999,
          background: SKY_BLUE,
          animation: `cascade-think-dot 1.2s ease-in-out ${d * 0.18}s infinite`,
        }}
      />
    ))}
  </div>
);

const DoneCheck: FC = () => (
  <div
    className="cascade-animate"
    aria-hidden="true"
    style={{
      width: 14,
      height: 14,
      borderRadius: 999,
      background: "rgba(37,99,235,0.15)",
      border: "1px solid rgba(37,99,235,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      animation: "cascade-check-pop 420ms cubic-bezier(0.22, 1, 0.36, 1)",
    }}
  >
    <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6.5L5 9L9.5 3.5"
        stroke={SKY_BLUE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const SPLITTING_LINE_WIDTHS = [88, 76, 92, 64];

const SplittingSkeletonLines: FC = () => {
  const { skeletonRgb } = useContainerStyle();
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        marginTop: 8,
      }}
    >
      {SPLITTING_LINE_WIDTHS.map((w, i) => (
        <div
          key={i}
          style={{
            height: 5,
            width: `${w}%`,
            borderRadius: 2,
            background: `rgba(${skeletonRgb},0.08)`,
          }}
        />
      ))}
    </div>
  );
};

interface SectionSkeletonCardProps {
  index: number;
}

const SectionSkeletonCard: FC<SectionSkeletonCardProps> = ({ index }) => {
  const { skeletonRgb } = useContainerStyle();
  return (
    <div
      aria-hidden="true"
      style={{
        position: "relative",
        paddingLeft: 18,
        opacity: 0,
        transform: "translateY(12px) scale(0.98)",
        animation: `cascade-card-enter 520ms cubic-bezier(0.22, 1, 0.36, 1) ${index * ENTER_STAGGER_MS}ms forwards`,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 2,
          bottom: 2,
          width: 2,
          borderRadius: 2,
          background: `rgba(${skeletonRgb},0.06)`,
        }}
      />
      <div
        style={{
          height: 10,
          width: "38%",
          borderRadius: 2,
          background: `rgba(${skeletonRgb},0.12)`,
          marginBottom: 10,
        }}
      />
      <SplittingSkeletonLines />
    </div>
  );
};
